import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { Search, ArrowRight, RefreshCw, ShieldCheck, Split, MapPinned, Newspaper, Route as RouteIcon, Leaf, TreePine, Wind } from "lucide-react";
import { formatCo2 } from "@/lib/constants";

const HERO = "https://static.prod-images.emergentagent.com/jobs/278101d8-fe0e-4628-814f-1760f1674f44/images/3488f6958ba62f7011565b7504377cf17bdbf1ccc3cffe4af2a3a4ff3020d61a.jpeg";
const WAREHOUSE = "https://images.unsplash.com/photo-1586528116022-aeda1613c63d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjB3YXJlaG91c2UlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODk2Mjg2MTN8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const navigate = useNavigate();
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [news, setNews] = useState([]);
  const [impact, setImpact] = useState(null);

  useEffect(() => {
    api.get("/news?limit=4").then(({ data }) => setNews(data)).catch(() => {});
    api.get("/impact/stats").then(({ data }) => setImpact(data)).catch(() => {});
  }, []);

  const search = () => {
    const p = new URLSearchParams();
    if (origin) p.set("origin", origin);
    if (destination) p.set("destination", destination);
    navigate(`/cerca?${p.toString()}`);
  };

  return (
    <div className="App bg-background">
      <Navbar />

      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="Camion su strada in una giornata luminosa" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/20" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 pt-24 pb-28 md:pt-32 md:pb-36">
          <p className="label-eyebrow text-accent ts-rise">Logistica collaborativa &amp; sostenibile</p>
          <h1 className="font-display font-extrabold tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl max-w-3xl mt-4 ts-rise" style={{ animationDelay: "0.08s" }}>
            Ottimizza ogni chilometro.
          </h1>
          <p className="text-white/85 text-base md:text-lg max-w-xl mt-5 ts-rise" style={{ animationDelay: "0.16s" }}>
            Spazio vuoto, valore pieno. Trova un trasportatore per la tua merce oppure riempi le tue tratte a vuoto — riducendo emissioni e sprechi.
          </p>

          {impact && impact.co2_saved_kg > 0 && (
            <div className="mt-6 inline-flex items-center gap-2 bg-emerald-500/95 text-white rounded-sm px-4 py-2 ts-rise" style={{ animationDelay: "0.2s" }} data-testid="hero-co2-pill">
              <Leaf size={16} />
              <span className="text-sm font-semibold">Già {formatCo2(impact.co2_saved_kg)} di CO₂ risparmiata dalla community</span>
            </div>
          )}

          {/* floating search */}
          <div className="mt-10 bg-background/95 backdrop-blur-xl border border-border rounded-sm p-4 max-w-3xl ts-rise shadow-2xl" style={{ animationDelay: "0.24s" }}>
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1 relative">
                <MapPinned size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary" />
                <Input value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="Origine (es. Milano)" className="rounded-sm pl-9 h-12" data-testid="hero-origin-input" />
              </div>
              <div className="flex-1 relative">
                <ArrowRight size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-accent" />
                <Input value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="Destinazione (es. Napoli)" className="rounded-sm pl-9 h-12" data-testid="hero-destination-input" />
              </div>
              <Button onClick={search} className="rounded-sm h-12 px-8 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold gap-2" data-testid="hero-search-btn">
                <Search size={18} /> Cerca trasporto
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES bento */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <p className="label-eyebrow text-accent">Come funziona</p>
        <h2 className="font-display font-bold tracking-tight text-3xl md:text-4xl mt-3 max-w-2xl">Un marketplace per i viaggi di ritorno a vuoto.</h2>

        <div className="grid gap-4 md:grid-cols-12 mt-10">
          <Feature className="md:col-span-7" icon={RefreshCw} title="Riempi i viaggi a vuoto"
            text="I trasportatori pubblicano le tratte vuote con tappe intermedie, tipologia di merce, date e range di prezzo. Nessun chilometro sprecato." />
          <Feature className="md:col-span-5" icon={Split} title="Trasporti parziali"
            text="Sfrutta anche solo una porzione della tratta: carico e scarico su tappe intermedie, se il trasportatore è disponibile." />
          <Feature className="md:col-span-5" icon={ShieldCheck} title="Polizza al 3%"
            text="Assicura la spedizione con una compagnia esterna al costo del 3% del valore assicurato, direttamente in fase di prenotazione." />
          <Feature className="md:col-span-7" icon={MapPinned} title="Privati e professionisti"
            text="Utenti privati e trasportatori professionali pubblicano e cercano viaggi. Ogni categoria ha il proprio pannello di controllo dedicato." image={WAREHOUSE} />
        </div>
      </section>

      {/* GREEN IMPACT */}
      <section className="bg-emerald-950 text-white">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <p className="label-eyebrow text-emerald-400 flex items-center gap-2"><Leaf size={14} /> Impatto ambientale</p>
          <h2 className="font-display font-bold tracking-tight text-3xl md:text-4xl mt-3 max-w-2xl">Ogni viaggio a vuoto riempito è CO₂ in meno nell'aria.</h2>
          <p className="text-white/70 mt-4 max-w-2xl">
            Un mezzo pesante che torna vuoto emette in media <strong className="text-emerald-300">0,8–1 kg di CO₂ per chilometro</strong>. Riempiendo quel viaggio si evita una spedizione duplicata: meno emissioni, meno traffico, più efficienza.
          </p>

          <div className="grid sm:grid-cols-3 gap-4 mt-10">
            <ImpactStat icon={Wind} value={impact ? formatCo2(impact.co2_saved_kg) : "—"} label="CO₂ risparmiata finora" />
            <ImpactStat icon={TreePine} value={impact ? `${impact.trees_equivalent}` : "—"} label="Alberi equivalenti / anno" />
            <ImpactStat icon={RouteIcon} value={impact ? `${impact.total_km.toLocaleString("it-IT")} km` : "—"} label="Km ottimizzati" />
          </div>
        </div>
      </section>

      {/* NEWS preview */}
      <section className="mx-auto max-w-7xl px-6 pb-8">
        <div className="flex items-center justify-between">
          <div>
            <p className="label-eyebrow text-accent flex items-center gap-2"><Newspaper size={14} /> News di settore</p>
            <h2 className="font-display font-bold tracking-tight text-3xl mt-2">Trasporto terrestre, ogni giorno.</h2>
          </div>
          <Button variant="outline" className="rounded-sm gap-2" onClick={() => navigate("/news")} data-testid="landing-allnews-btn">
            Tutte le news <ArrowRight size={16} />
          </Button>
        </div>
        <div className="grid gap-4 md:grid-cols-4 mt-8">
          {news.map((a) => (
            <a key={a.article_id} href={a.link} target="_blank" rel="noreferrer" className="border border-border rounded-sm p-4 hover:border-primary transition-colors bg-card" data-testid="landing-news-card">
              <span className="label-eyebrow text-accent">{a.source}</span>
              <h3 className="font-display font-semibold text-sm mt-2 line-clamp-3">{a.title}</h3>
              <p className="text-xs text-muted-foreground mt-2">{formatDate(a.published_at)}</p>
            </a>
          ))}
          {news.length === 0 && <p className="text-sm text-muted-foreground col-span-4">Caricamento articoli…</p>}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 py-16">
        <div className="bg-primary text-primary-foreground rounded-sm p-10 md:p-16 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-display font-extrabold tracking-tighter text-3xl md:text-4xl">Hai un mezzo che torna vuoto?</h2>
            <p className="text-primary-foreground/80 mt-3 max-w-lg">Pubblica le tue tratte in pochi secondi e ricevi richieste di trasporto.</p>
          </div>
          <Button onClick={() => navigate("/accedi?tab=register")} className="rounded-sm h-14 px-10 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold text-base gap-2 shrink-0" data-testid="cta-register-btn">
            <RouteIcon size={18} /> Diventa trasportatore
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ImpactStat({ icon: Icon, value, label }) {
  return (
    <div className="border border-emerald-800 bg-emerald-900/40 rounded-sm p-6" data-testid="impact-stat">
      <Icon size={26} className="text-emerald-400" />
      <p className="font-display font-extrabold text-4xl mt-4">{value}</p>
      <p className="text-sm text-white/60 mt-1">{label}</p>
    </div>
  );
}

function Feature({ icon: Icon, title, text, className = "", image }) {
  return (
    <div className={`relative overflow-hidden border border-border rounded-sm p-6 bg-card ${className}`}>
      {image && <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-10" />}
      <div className="relative">
        <div className="w-11 h-11 rounded-sm bg-primary/10 flex items-center justify-center text-primary"><Icon size={22} /></div>
        <h3 className="font-display font-bold text-xl mt-4">{title}</h3>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{text}</p>
      </div>
    </div>
  );
}
